import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { ChevronRight, BookOpen, ChevronDown, ChevronUp, Menu, X, ArrowRight } from 'lucide-react';
import { getStructure, getItemsByRange } from '../services/api';
import './Chapters.css';

const Chapters = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const partParam = searchParams.get('part');
  
  const [structure, setStructure] = useState(null);
  const [selectedPart, setSelectedPart] = useState(null);
  const [allSections, setAllSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedChapters, setExpandedChapters] = useState(new Set());
  const [activeSectionId, setActiveSectionId] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const contentRef = useRef(null);
  const sectionRefs = useRef({});
  const sidebarRef = useRef(null);
  const isScrollingRef = useRef(false);

  // 获取所有小节列表
  const getAllSectionsFromPart = useCallback((part) => {
    const sections = [];
    part.chapters.forEach((chapter, chapterIndex) => {
      if (chapter.subsections?.length > 0) {
        chapter.subsections.forEach((subsection, subIndex) => {
          if (subsection.item_range) {
            sections.push({
              ...subsection,
              chapter,
              chapterIndex,
              id: `${part.id}-c${chapterIndex}-s${subIndex}`,
            });
          }
        });
      } else if (chapter.sections?.length > 0) {
        chapter.sections.forEach((section, secIndex) => {
          if (section.subsections?.length > 0) {
            section.subsections.forEach((subsection, subIndex) => {
              sections.push({
                ...subsection,
                chapter,
                section,
                chapterIndex,
                id: `${part.id}-c${chapterIndex}-sec${secIndex}-s${subIndex}`,
              });
            });
          } else if (section.item_range) {
            sections.push({
              ...section,
              chapter,
              section,
              chapterIndex,
              id: `${part.id}-c${chapterIndex}-sec${secIndex}`,
            });
          }
        });
      }
    });
    return sections;
  }, []);

  // 加载所有内容
  const loadAllContent = useCallback(async (part) => {
    setLoading(true);
    const sections = getAllSectionsFromPart(part);
    
    const sectionsWithItems = await Promise.all(
      sections.map(async (section) => {
        if (section.item_range) {
          const range = parseRange(section.item_range);
          if (range) {
            try {
              const items = await getItemsByRange(range.start, range.end);
              return { ...section, items };
            } catch (error) {
              console.error('Error fetching items:', error);
              return { ...section, items: [] };
            }
          }
        }
        return { ...section, items: [] };
      })
    );
    
    setAllSections(sectionsWithItems);
    if (sectionsWithItems.length > 0) {
      setActiveSectionId(sectionsWithItems[0].id);
      setExpandedChapters(new Set([sectionsWithItems[0].chapterIndex]));
    }
    setLoading(false);
  }, [getAllSectionsFromPart]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getStructure();
        setStructure(data);
        
        let part = null;
        if (partParam) {
          part = data.parts.find(p => p.id === partParam);
        }
        if (!part && data.parts.length > 0) {
          part = data.parts[0];
        }
        
        if (part) {
          setSelectedPart(part);
          await loadAllContent(part);
        }
      } catch (error) {
        console.error('Error fetching structure:', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [partParam, loadAllContent]);

  // Intersection Observer 监听章节进入视口
  useEffect(() => {
    if (allSections.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isScrollingRef.current) return;
        
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.dataset.sectionId;
            setActiveSectionId(sectionId);
            
            const section = allSections.find(s => s.id === sectionId);
            if (section) {
              setExpandedChapters(prev => new Set([...prev, section.chapterIndex]));
            }
          }
        });
      },
      {
        root: null,
        rootMargin: '-15% 0px -70% 0px',
        threshold: 0,
      }
    );

    Object.values(sectionRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [allSections]);

  // PC端同步左侧导航滚动位置
  useEffect(() => {
    if (!activeSectionId || !sidebarRef.current || window.innerWidth <= 1024) return;
    
    const activeElement = sidebarRef.current.querySelector(`[data-section-id="${activeSectionId}"]`);
    if (activeElement) {
      activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeSectionId]);

  const handlePartSelect = async (part) => {
    setSelectedPart(part);
    await loadAllContent(part);
    setIsMobileMenuOpen(false);
  };

  const toggleChapter = (index) => {
    const newExpanded = new Set(expandedChapters);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedChapters(newExpanded);
  };

  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      isScrollingRef.current = true;
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSectionId(sectionId);
      setIsMobileMenuOpen(false);
      
      setTimeout(() => {
        isScrollingRef.current = false;
      }, 600);
    }
  };

  const parseRange = (rangeStr) => {
    if (!rangeStr) return null;
    if (rangeStr.includes('-')) {
      const [start, end] = rangeStr.split('-').map(n => parseInt(n.trim(), 10));
      return { start, end };
    } else {
      const num = parseInt(rangeStr, 10);
      return { start: num, end: num };
    }
  };

  // 获取当前位置标题
  const getCurrentLocationTitle = () => {
    if (!activeSectionId || allSections.length === 0) return '选择章节';
    const section = allSections.find(s => s.id === activeSectionId);
    if (!section) return '选择章节';
    return section.title || `${section.number} 条目列表`;
  };

  // 获取当前篇章名称
  const getCurrentPartName = () => {
    return selectedPart?.name || '选择篇章';
  };

  // 渲染目录内容
  const renderSidebarContent = () => (
    <>
      <div className="sidebar-header">
        <h2 className="sidebar-title">目录导航</h2>
        <button 
          className="mobile-menu-close" 
          onClick={() => setIsMobileMenuOpen(false)}
          aria-label="关闭目录"
        >
          <X size={24} />
        </button>
      </div>
      
      <div className="part-selector">
        {structure?.parts.map((part) => (
          <button
            key={part.id}
            className={`part-btn ${selectedPart?.id === part.id ? 'active' : ''}`}
            onClick={() => handlePartSelect(part)}
          >
            <BookOpen size={18} />
            <span>{part.name}</span>
          </button>
        ))}
      </div>

      {selectedPart && (
        <div className="chapter-list">
          <h3 className="chapter-list-title">{selectedPart.name}</h3>
          {selectedPart.chapters.map((chapter, index) => (
            <div key={index} className="chapter-item">
              <button
                className="chapter-header"
                onClick={() => toggleChapter(index)}
              >
                <span className="chapter-number">{chapter.number}</span>
                <span className="chapter-name">{chapter.title}</span>
                {chapter.subsections || chapter.sections ? (
                  expandedChapters.has(index) ? (
                    <ChevronUp size={16} />
                  ) : (
                    <ChevronDown size={16} />
                  )
                ) : null}
              </button>
              
              {expandedChapters.has(index) && (
                <div className="subsection-list">
                  {chapter.subsections?.map((subsection, subIndex) => (
                    subsection.item_range && (
                      <button
                        key={subIndex}
                        data-section-id={`${selectedPart.id}-c${index}-s${subIndex}`}
                        className={`subsection-btn ${
                          activeSectionId === `${selectedPart.id}-c${index}-s${subIndex}` ? 'active' : ''
                        }`}
                        onClick={() => scrollToSection(`${selectedPart.id}-c${index}-s${subIndex}`)}
                      >
                        <span>{subsection.number} {subsection.title || '查看条目'}</span>
                        <span className="subsection-range">{subsection.item_range}</span>
                      </button>
                    )
                  ))}
                  
                  {chapter.sections?.map((section, secIndex) => (
                    <div key={secIndex} className="section-group">
                      <div className="section-header">
                        <span>{section.number} {section.title}</span>
                      </div>
                      {section.subsections?.map((subsection, subIndex) => (
                        <button
                          key={subIndex}
                          data-section-id={`${selectedPart.id}-c${index}-sec${secIndex}-s${subIndex}`}
                          className={`subsection-btn nested ${
                            activeSectionId === `${selectedPart.id}-c${index}-sec${secIndex}-s${subIndex}` ? 'active' : ''
                          }`}
                          onClick={() => scrollToSection(`${selectedPart.id}-c${index}-sec${secIndex}-s${subIndex}`)}
                        >
                          <span>{subsection.number} {subsection.title}</span>
                          <span className="subsection-range">{subsection.item_range}</span>
                        </button>
                      ))}
                      {!section.subsections?.length && section.item_range && (
                        <button
                          data-section-id={`${selectedPart.id}-c${index}-sec${secIndex}`}
                          className={`subsection-btn nested ${
                            activeSectionId === `${selectedPart.id}-c${index}-sec${secIndex}` ? 'active' : ''
                          }`}
                          onClick={() => scrollToSection(`${selectedPart.id}-c${index}-sec${secIndex}`)}
                        >
                          <span>查看条目</span>
                          <span className="subsection-range">{section.item_range}</span>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );

  if (loading) {
    return (
      <div className="loading">
        <div className="loading-spinner"></div>
        <p>加载中...</p>
      </div>
    );
  }

  return (
    <div className="chapters-page">
      <div className="chapters-layout">
        {/* PC端侧边栏 */}
        <aside className="chapters-sidebar desktop-only" ref={sidebarRef}>
          {renderSidebarContent()}
        </aside>

        {/* 移动端抽屉遮罩 */}
        {isMobileMenuOpen && (
          <div 
            className="mobile-drawer-overlay" 
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
        
        {/* 移动端抽屉 */}
        <aside className={`mobile-drawer ${isMobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-drawer-content">
            {renderSidebarContent()}
          </div>
        </aside>

        {/* 内容区域 */}
        <main className="chapters-content" ref={contentRef}>
          {/* 移动端顶部导航栏 */}
          <div className="mobile-nav-bar">
            <button 
              className="mobile-nav-btn"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label="打开目录"
            >
              <Menu size={20} />
              <span className="mobile-nav-text">目录</span>
            </button>
            <div className="mobile-nav-info">
              <span className="mobile-nav-part">{getCurrentPartName()}</span>
              <span className="mobile-nav-divider">·</span>
              <span className="mobile-nav-section">{getCurrentLocationTitle()}</span>
            </div>
          </div>

          {!selectedPart ? (
            <div className="empty-state">
              <BookOpen size={64} className="empty-icon" />
              <h3>选择篇章开始浏览</h3>
              <p>请从左侧目录选择学训一百条</p>
            </div>
          ) : allSections.length === 0 ? (
            <div className="empty-state">
              <ChevronRight size={64} className="empty-icon" />
              <h3>暂无内容</h3>
              <p>该篇章下没有可显示的内容</p>
            </div>
          ) : (
            <div className="content-scroll-container">
              {allSections.map((section, index) => (
                <div
                  key={section.id}
                  ref={(el) => { sectionRefs.current[section.id] = el; }}
                  data-section-id={section.id}
                  className="content-section"
                >
                  <div className="content-panel">
                    <div className="content-header">
                      <nav className="breadcrumb">
                        <span>{selectedPart.name}</span>
                        <ChevronRight size={16} />
                        <span>{section.chapter?.title}</span>
                        {section.section && (
                          <>
                            <ChevronRight size={16} />
                            <span>{section.section.number} {section.section.title}</span>
                          </>
                        )}
                        {section.title && (
                          <>
                            <ChevronRight size={16} />
                            <span>{section.number} {section.title}</span>
                          </>
                        )}
                      </nav>
                      <h2 className="content-title">
                        {section.number} {section.title || '条目列表'}
                      </h2>
                      {section.subtitle && (
                        <p className="content-subtitle">{section.subtitle}</p>
                      )}
                      <span className="content-range">条目 {section.item_range}</span>
                    </div>

                    <div className="items-list">
                      {section.items?.map((item, itemIndex) => (
                        <div
                          key={item.number}
                          className="item-card animate-slide-in"
                          style={{ animationDelay: `${itemIndex * 0.03}s` }}
                        >
                          <span className="item-number">{item.number}</span>
                          <p className="item-content">{item.content}</p>
                          <button
                            className="item-detail-btn"
                            onClick={() => navigate(`/item/${item.number}`)}
                            aria-label="查看详情"
                          >
                            <ArrowRight size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {index < allSections.length - 1 && (
                    <div className="section-divider">
                      <span className="divider-line"></span>
                      <span className="divider-text">继续下滑查看更多</span>
                      <span className="divider-line"></span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Chapters;
